import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { semanticTokens } from "@packages/config/theme";
import { sfIcons } from "@/lib/sf-icons";
import { SFIcon } from "@/components/ui/sf-icon";
import { useState } from "react";

interface AccountDetailsCardProps {
  accountType?: "individual" | "business" | string | null;
  accountStatus?: "active" | "restricted" | "blocked" | string | null;
  accountCreatedAt?: string | null;
  lastLoginAt?: string | null;
  accountId?: string | null;
  email?: string | null;
}

/**
 * AccountDetailsCard - Collapsible account details information
 * 
 * Shows account information:
 * - Account Type (Individual / Business)
 * - Account Status (Active / Restricted / Blocked)
 * - Account Created date
 * - Last Login date
 * - Account ID (masked, for support)
 * 
 * Collapsed by default to save space.
 * Includes "Contact Support" link.
 * 
 * Design Canon Compliance:
 * - Tokens-only (semanticTokens)
 * - Motion tokens (240ms canonical)
 * - WCAG 2.2 AA accessibility
 * - Collapsible (Radix UI)
 */
export function AccountDetailsCard({
  accountType,
  accountStatus,
  accountCreatedAt,
  lastLoginAt,
  accountId,
  email
}: AccountDetailsCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    } catch {
      return "—";
    }
  };

  // Format account ID (masked)
  const formatAccountId = (id: string | null | undefined): string => {
    if (!id) return "—";
    if (id.length <= 8) return id;
    return `${id.slice(0, 4)}…${id.slice(-4)}`;
  };

  // Get status badge variant
  const getStatusVariant = (status: string | null | undefined): "default" | "secondary" | "destructive" => {
    const s = (status || "").toLowerCase();
    if (s === "active") return "default";
    if (s === "restricted") return "secondary";
    if (s === "blocked") return "destructive";
    return "default";
  };

  // Get account type display
  const getAccountTypeDisplay = (type: string | null | undefined): string => {
    const t = (type || "").toLowerCase();
    if (t === "business") return "Business";
    if (t === "individual") return "Individual";
    return type || "—";
  };

  return (
    <Card role="region" aria-label="Account details">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader
            className={cn(
              "cursor-pointer hover:bg-muted/30 pb-3",
              semanticTokens.motion.standard,
              semanticTokens.spacing.md.replace('p-', 'pb-'),
              "focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            )}
            aria-expanded={isOpen}
            aria-controls="account-details-content"
          >
            <div className="flex items-center justify-between">
              <CardTitle className={cn(semanticTokens.typography.h4, "flex items-center gap-2")}>
                <SFIcon icon={sfIcons.info} className="h-5 w-5" aria-hidden="true" />
                Account Details
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="p-0 h-8 w-8"
                aria-label={isOpen ? "Hide account details" : "Show account details"}
              >
                {isOpen ? (
                  <SFIcon icon={sfIcons.chevronUp} className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <SFIcon icon={sfIcons.chevronDown} className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent id="account-details-content">
          <CardContent className={cn("space-y-4 pt-0", semanticTokens.motion.standard)}>
            {/* Account Information Grid */}
            <div
              className="grid grid-cols-1 xs:grid-cols-1 md:grid-cols-2 gap-4 xs:gap-4"
              role="list"
              aria-label="Account information"
            >
              {/* Account Type */}
              <div role="listitem" className="space-y-1">
                <div className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                  Account Type
                </div>
                <div className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
                  {getAccountTypeDisplay(accountType)}
                </div>
              </div>

              {/* Account Status - Canon v1.4.5: warning styling for restricted */}
              <div role="listitem" className="space-y-1">
                <div className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                  Account Status
                </div>
                <div className={cn(
                  semanticTokens.typography.label,
                  (accountStatus || "").toLowerCase() === "restricted"
                    ? semanticTokens.warning.fg
                    : (accountStatus || "").toLowerCase() === "blocked"
                      ? semanticTokens.danger.fg
                      : semanticTokens.fg.default
                )}>
                  {accountStatus || "Active"}
                </div>
              </div>

              {/* Account Created */}
              <div role="listitem" className="space-y-1">
                <div className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                  Account Created
                </div>
                <div className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
                  {formatDate(accountCreatedAt)}
                </div>
              </div>

              {/* Last Login */}
              <div role="listitem" className="space-y-1">
                <div className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                  Last Login
                </div>
                <div className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
                  {formatDate(lastLoginAt)}
                </div>
              </div>

              {/* Account ID */}
              <div role="listitem" className="space-y-1 md:col-span-2">
                <div className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                  Account ID
                </div>
                <div className={cn(semanticTokens.typography.label, semanticTokens.fg.default, "font-mono")}>
                  {formatAccountId(accountId)}
                </div>
                <div className={cn(semanticTokens.typography.captionSmall, semanticTokens.fg.muted, "mt-1")}>
                  For support purposes
                </div>
              </div>
            </div>

            {/* Actions */}
            <div
              className={cn(
                "flex flex-wrap gap-2 pt-2 border-t",
                semanticTokens.border.default,
                semanticTokens.motion.standard
              )}
              role="group"
              aria-label="Account actions"
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const subject = encodeURIComponent("Account Support Request");
                  const body = encodeURIComponent(
                    `Account ID: ${accountId || "N/A"}\nEmail: ${email || "N/A"}\n\nIssue Description:\n`
                  );
                  window.location.href = `mailto:support@supabank.xyz?subject=${subject}&body=${body}`;
                }}
                className={cn(semanticTokens.motion.standard)}
                aria-label="Contact support via email"
              >
                <SFIcon icon={sfIcons.mail} className="h-4 w-4 mr-2" aria-hidden="true" />
                Contact Support
              </Button>

              {/* Future: View Account History */}
              {/* <Button
                variant="ghost"
                size="sm"
                disabled
                className={cn(semanticTokens.motion.standard)}
                aria-label="View account history (coming soon)"
              >
                <ExternalLink className="h-4 w-4 mr-2" aria-hidden="true" />
                View History
                <span className={cn("ml-2 text-xs", semanticTokens.fg.muted)}>(Soon)</span>
              </Button> */}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

